<template>
    <div class="">
        <Teleport :to="headerMenuElement">
            <div class="flex items-center space-x-0.5">
                <DButton
                    data-cy="events-go-to-today-button"
                    theme="transparent"
                    icon="ph:calendar-dot"
                    :disabled="loading || refreshingWidgetPage"
                    @click="goToToday"
                />
                <DButton
                    data-cy="events-open-filters-button"
                    theme="transparent"
                    icon="ph:sliders-horizontal"
                    :disabled="loading || refreshingWidgetPage"
                    :indicator="!!filterCount"
                    :indicator-number="filterCount"
                    indicator-color="brand"
                    @click="openFilters"
                />
                <InlineSwitcher
                    v-if="switcher?.users"
                    :switcher="switcher"
                    size="small"
                    title="Calendar"
                    @switch="switchUser"
                />
                <div
                    v-else-if="switching"
                    class="w-14"
                />
            </div>
        </Teleport>

        <Teleport
            v-if="headerContextualElement"
            :to="headerContextualElement"
        >
            <template v-if="selectedView === VIEW_WEEK">
                <div class="p-2 py-3 flex items-center justify-between">
                    <DButton
                        data-cy="events-widget-week-prev-button"
                        theme="transparent"
                        icon="chevron-left-20-solid"
                        @click="addToDate(-1, 'week')"
                    />

                    <span
                        class="text-gray-900 font-medium"
                        data-cy="events-widget-week-display"
                    >
                        {{ weekDisplay }}
                    </span>

                    <DButton
                        data-cy="events-widget-week-next-button"
                        theme="transparent"
                        icon="chevron-right-20-solid"
                        @click="addToDate(1, 'week')"
                    />
                </div>
            </template>
            <template v-if="selectedView === VIEW_DAY">
                <div class="p-3 flex items-center justify-between">
                    <DButton
                        data-cy="events-widget-day-prev-button"
                        theme="transparent"
                        icon="chevron-left-20-solid"
                        @click="addToDate(-1, 'day')"
                    />

                    <span
                        class="text-gray-900 font-medium"
                        data-cy="events-widget-day-display"
                    >
                        <span :class="{ 'text-(--color-brand-700) font-semibold': dayNameDisplay === 'Today' }">{{ dayNameDisplay }}</span>, {{ dayDisplay }}
                    </span>

                    <DButton
                        data-cy="events-widget-day-next-button"
                        theme="transparent"
                        icon="chevron-right-20-solid"
                        @click="addToDate(1, 'day')"
                    />
                </div>
            </template>
        </Teleport>

        <div>
            <template v-if="selectedView === VIEW_MONTH">
                <div class="py-1">
                    <DDatepicker
                        v-model="selectedDate"
                        :events="eventIndicators"
                        min-date="1970-01-01"
                        max-date="2037-01-01"
                        inline
                        fullwidth
                        :display-dropdowns="false"
                        :locale="schoolLocale"
                        @change-month="onMonthChange"
                        @update:model-value="onDatePicker"
                    />
                </div>
            </template>

            <div>
                <DependencyErrorAlert
                    v-if="!loading && !refreshingWidgetPage"
                    :widget-type="widgetType.events"
                    :dependencies="dependencies"
                    class="my-4"
                />

                <div
                    v-if="loading || refreshingWidgetPage"
                    class="flex justify-center py-12"
                >
                    <UILoading class="w-12 h-12" />
                </div>

                <CalendarEmpty
                    v-else-if="selectedView === VIEW_MONTH && !selectedEvents.length || !eventItems.length"
                    :filter-count="filterCount"
                    :clear-filters="clearFilters"
                    :subtitle="`Please check another ${selectedView === VIEW_WEEK ? 'week' : 'day'} or come back later.`"
                />

                <div
                    v-else
                    class="pb-(--beyond-safe-area-inset-bottom)"
                >
                    <template v-if="selectedView === VIEW_MONTH">
                        <div
                            v-for="(eventGroup, key) in selectedEventsWithHeadings"
                            :key="key"
                        >
                            <p class="p-4 content-xs-ux-medium text-neutral-empty uppercase border-b border-neutral-subtle sticky top-0 z-10 surface-primary">
                                {{ key }}
                            </p>

                            <EventItem
                                v-for="(event, index) in eventGroup"
                                :key="index"
                                :item="event.data"
                                :show-day="false"
                            />
                        </div>
                    </template>
                    <template v-else-if="selectedView === VIEW_WEEK">
                        <div
                            v-for="(eventGroup, key) in eventsWithHeadings"
                            :key="key"
                        >
                            <p class="p-4 content-xs-ux-medium text-neutral-empty uppercase border-b border-neutral-subtle sticky top-0 z-10 surface-primary">
                                {{ key }}
                            </p>

                            <EventItem
                                v-for="(event, index) in eventGroup"
                                :key="index"
                                :item="event.data"
                                :is-last="index === eventGroup.length - 1"
                                :show-day="false"
                            />
                        </div>
                    </template>
                    <template v-else-if="selectedView === VIEW_DAY">
                        <EventItem
                            v-for="(item, index) in eventItems"
                            :key="index"
                            :item="item.data"
                            :show-day="false"
                        />
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { modalController } from '@ionic/vue'
    import {
        DButton, DDatepicker,
    } from '@digistorm/spark'
    import {
        map, filter, groupBy, transform, isEmpty, differenceBy, each,
    } from 'lodash'
    import dependencyStatus from '@/constants/dependencyStatus'
    import widgetType from '@/constants/widgetType'
    import { useWidgetsStore } from '@/stores/widgets'
    import { api, preferences } from '@/services'
    import dayjs from '@/dayjs'
    import EventsFiltersModal from '@/components/next-gen/widget-pages/events/EventsFiltersModal.vue'
    import EventsViewModal from '@/components/next-gen/widget-pages/events/EventsViewModal.vue'
    import DependencyErrorAlert from '@/components/next-gen/widgets/shared/DependencyErrorAlert.vue'
    import UILoading from '@/components/ui/UILoading.vue'
    import CalendarEmpty from '@/components/app/CalendarEmpty.vue'
    import EventItem from '@/components/next-gen/widgets/events/EventItem.vue'
    import InlineSwitcher from '@/components/ui/InlineSwitcher.vue'
    import { useWidgetPagesStore } from '@/stores/widgetPages'
    import { useWidgetFilterHelpers } from '@/composables/widgetFilterHelpers'
    import { useWidgetFilters } from '@/composables/widgetFilters'
    import { useDateHelper } from '@/composables/useDateHelper'

    const emit = defineEmits(['widgetPageIdChange'])

    const props = defineProps({
        widgetPageId: String,
    })

    const calendarHelpers = useCalendarHelpers()
    const { snackbar } = useSnackbars()
    const widgetsStore = useWidgetsStore()
    const widgetPagesStore = useWidgetPagesStore()

    const headerMenu = inject('headerMenu', null)

    const { widgetPages } = storeToRefs(widgetPagesStore)

    const {
        VIEW_MONTH,
        VIEW_WEEK,
        VIEW_DAY,
        generateMultiDayEventsAsItems,
        getStartForView,
        getEndForView,
        getDateForNewMonth,
        getSchoolLocale,
    } = calendarHelpers
    const widgetHelpers = useWidgetHelpers()
    const widgetFilterHelpers = useWidgetFilterHelpers()

    const now = dayjs()
    const schoolLocale = getSchoolLocale() // This isn't expected to change during use

    const dateHelper = useDateHelper()

    const selectedDate = ref(now.format('YYYY-MM-DD'))
    const previouslyLoadedMonth = ref(now.month())
    const selectedView = ref(VIEW_MONTH)
    const supportedViews = [VIEW_DAY, VIEW_WEEK, VIEW_MONTH]

    const switching = ref(false)
    const shouldRefreshFilters = ref(false)

    const { selectedFilters, filterGroups, filterCount, fetchFilters, updateSelectedFilters, setFilterUrlWithoutFetching } = useWidgetFilters()

    const rangeStart = ref(now.startOf('month').startOf('week'))
    const rangeEnd = ref(now.endOf('month').endOf('week'))

    const headerMenuElement = computed(() => {
        return headerMenu?.value?.$el?.querySelector('#header-menu')
    })

    const headerContextualElement = computed(() => {
        return headerMenu?.value?.$el?.querySelector('#header-contextual')
    })

    const widgetPageMetadata = computed(() => widgetPages.value?.[props.widgetPageId])
    const widgetPage = computed(() => widgetPageMetadata.value?.data)
    const switcher = computed(() => widgetPage.value?.switcher)

    const widgetPageUrlRelative = computed(() => {
        return (widgetPageMetadata.value?.url ?? '').replace(api.defaults.baseURL, '')
    })

    const dependencies = computed(() => {
        // Transform dependencies to include active filters and date range
        return transform(widgetPage.value.dependencies ?? [], (dependencies, dependency) => {
            const url = new URL(dependency.url)
            url.searchParams.append('start', rangeStart.value.toISOString())
            url.searchParams.append('end', rangeEnd.value.toISOString())

            const resolvedDependency = widgetFilterHelpers.applyFiltersToDependency(
                filterGroups.value,
                selectedFilters.value,
                {
                    ...dependency,
                    url: url.toString(),
                    original_url: dependency.url,
                },
            )

            // Filters may result in the dependency being removed (returned as null)
            if (!resolvedDependency) {
                return
            }

            dependencies.push(resolvedDependency)
        }, [])
    })

    const dependencyConfigs = computed(() => {
        return widgetsStore.dependencies(widgetType.events)
    })

    const errors = computed(() => {
        const errors = []
        each(dependencies.value, (dependency) => {
            const config = dependencyConfigs.value[dependency.url]
            if (!config || !config.error) {
                return
            }
            errors.push(config.error)
        })
        return errors
    })

    const loading = computed(() => widgetsStore.isLoading(widgetType.events, dependencies.value))

    const rawItems = computed(() => {
        return widgetsStore.items(widgetType.events, dependencies.value)
    })

    const eventItems = computed(() => {
        return generateMultiDayEventsAsItems(now.value, rawItems.value, rangeStart.value, rangeEnd.value, (item) => {
            const el = document.createElement('div')
            el.innerHTML = item.event.title
            return (el.textContent ?? el.innerText ?? '').toLowerCase()
        })
    })

    const eventsWithHeadings = computed(() => {
        return groupBy(eventItems.value, 'heading')
    })

    const selectedEvents = computed(() => {
        return filter(eventItems.value, (event) => {
            return event.date === dayjs(selectedDate.value).format('YYYY-MM-DD')
        })
    })

    const selectedEventsWithHeadings = computed(() => {
        return groupBy(selectedEvents.value, 'heading')
    })

    const eventIndicators = computed(() => {
        return map(eventItems.value, 'date')
    })

    const weekDisplay = computed(() => {
        return `Week of ${dateHelper.value.short().formatDate(getStartForView(selectedView.value, selectedDate.value), false)}`
    })

    const dayNameDisplay = computed(() => {
        if (dayjs(selectedDate.value).isToday()) {
            return 'Today'
        }
        return dayjs(selectedDate.value).format('dddd')
    })

    const dayDisplay = computed(() => {
        return dateHelper.value.short().formatDate(dayjs(selectedDate.value), false)
    })

    const preferenceKeyForView = computed(() => {
        return `widget-page:${widgetPageUrlRelative.value}:selected-view`
    })

    const refreshingWidgetPage = computed(() => widgetPageMetadata.value?.status === dependencyStatus.pending)

    const fetchDependencies = () => {
        // Set new range to be used across the component
        rangeStart.value = getStartForView(selectedView.value, selectedDate.value)
        rangeEnd.value = getEndForView(selectedView.value, selectedDate.value)
        widgetHelpers.fetchDependenciesHandlingRestartIfRequired(widgetType.events, dependencies.value)
    }

    const addToDate = (amount, type) => {
        selectedDate.value = dayjs(selectedDate.value).add(amount, type).format('YYYY-MM-DD')
        fetchDependencies()
    }

    function onMonthChange({ month, year } = { month: null, year: null }) {
        selectedDate.value = getDateForNewMonth(selectedDate.value, month, year)
        previouslyLoadedMonth.value = dayjs(selectedDate.value).month()
        fetchDependencies()
    }

    function onDatePicker(newValue, allowFetch = true) {
        const newMonth = dayjs(newValue).month()
        if (newMonth !== previouslyLoadedMonth.value && selectedView.value === VIEW_MONTH) {
            previouslyLoadedMonth.value = newMonth
            if (allowFetch) {
                fetchDependencies()
            }
        }
    }

    function goToToday() {
        selectedDate.value = dayjs().format('YYYY-MM-DD')
        if (selectedView.value === VIEW_MONTH) {
            onDatePicker(selectedDate.value)
        } else {
            fetchDependencies()
        }
    }

    const updateUserView = async (view) => {
        if (view === selectedView.value || !supportedViews.includes(view)) {
            return
        }
        selectedView.value = view

        // Save preference
        await preferences.setItem(preferenceKeyForView.value, view)
    }

    const loadUserViewFromPreferences = async (defaultView) => {
        const view = await preferences.getItem(preferenceKeyForView.value)
        if (!supportedViews.includes(view)) {
            selectedView.value = defaultView
            return
        }
        selectedView.value = view
    }

    const openFilters = async () => {
        let modal = null
        if (filterGroups.value.length === 0) {
            modal = await modalController.create({
                cssClass: 'ds-modal-bottom-sheet',
                component: EventsViewModal,
                componentProps: {
                    currentView: selectedView.value,
                    supportedViews,
                    onSelected: async (newView) => {
                        await updateUserView(newView)
                        fetchDependencies()
                        modal.dismiss()
                    },
                    onCancelled: () => {
                        modal.dismiss()
                    },
                },
                initialBreakpoint: 0.7,
                breakpoints: [0, 0.7],
            })
        } else {
            modal = await modalController.create({
                cssClass: 'ds-modal-fullscreen',
                component: EventsFiltersModal,
                componentProps: {
                    title: 'Filter',
                    filterGroups: filterGroups.value,
                    selectedFilters: selectedFilters.value,
                    currentView: selectedView.value,
                    supportedViews,
                    applyChanges: async (view, value) => {
                        await updateSelectedFilters(value)
                        await updateUserView(view)
                        fetchDependencies()
                    },
                },
            })
        }
        await modal.present()
    }

    const clearFilters = () => {
        updateSelectedFilters({})
        fetchDependencies()
    }

    const onRefreshingWidgetPage = (widgetId) => {
        if (widgetId !== props.widgetPageId) {
            return
        }
        widgetsStore.markDependenciesForRefresh(widgetType.events, dependencies.value)
    }

    const onRefreshedWidgetPage = (widgetId) => {
        if (widgetId !== props.widgetPageId) {
            return
        }
        shouldRefreshFilters.value = true
    }

    // TODO: Clean up widgets that were loaded / switched
    const switchUser = (user) => {
        switching.value = true

        widgetPagesStore.fetchWidgetPage(user.url, null, false, { view: selectedView.value, date: selectedDate.value })
            .then((id) => {
                emit('widgetPageIdChange', id)
            })
            .catch((e) => {
                snackbar({
                    message: 'Error switching user',
                    type: 'error',
                })
            })
            .finally(() => {
                switching.value = false
            })
    }

    const onWidgetPageChange = async () => {
        if (!widgetPage.value) {
            return
        }
        const { extra, data } = widgetPageMetadata.value

        if (extra?.view) {
            // Keep the view the user was looking at (when switching users), but don't record it as preference
            selectedView.value = supportedViews.includes(extra.view)
                ? extra.view
                : (data.default_view ?? VIEW_MONTH)
        } else {
            // Show users preferred view if available
            await loadUserViewFromPreferences(data.default_view ?? VIEW_MONTH)
        }

        if (extra?.date) {
            selectedDate.value = extra.date
            if (selectedView.value === VIEW_MONTH) {
                // Don't allow fetch dependencies here as we call at the end
                onDatePicker(selectedDate.value, false)
            }
        }

        const filtersUrl = widgetPage.value?.filters_url
        await setFilterUrlWithoutFetching(filtersUrl)

        // If there were selected filters in the preferences we should await fetching of filters so that dependencies aren't loaded twice
        if (!isEmpty(selectedFilters.value) && isEmpty(filterGroups.value)) {
            await fetchFilters(filtersUrl, shouldRefreshFilters.value)
                .catch((e) => console.error('Error fetching filters on widget page metadata change', e))
        } else {
            fetchFilters(filtersUrl, shouldRefreshFilters.value)
        }

        shouldRefreshFilters.value = false
        fetchDependencies()
    }

    watch(dependencies, (after, before) => {
        const unusedDependencies = differenceBy(before, after, (dep) => dep.url)
        widgetsStore.cancelPendingDependencies(widgetType.events, unusedDependencies)
    })

    watch(widgetPage, onWidgetPageChange)

    onMounted(async () => {
        dayjs.locale(schoolLocale)
        events.on('refreshing-widget-page', onRefreshingWidgetPage)
        events.on('refreshed-widget-page', onRefreshedWidgetPage)

        onWidgetPageChange(widgetPageMetadata.value)
    })

    onUnmounted(() => {
        events.off('refreshing-widget-page', onRefreshingWidgetPage)
        events.off('refreshed-widget-page', onRefreshedWidgetPage)
    })
</script>
